import * as _ from 'lodash';
import qs from 'qs';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { InjectedRouter } from 'react-router';

import { DependencySource } from '../../../../../shared/vendoredDependencies';
import { parsePaginationQueryParams } from '../../../../helpers/pagination';
import useDebouncedCallback from '../../../../hooks/useDebouncedCallback';
import * as DependenciesV2Store from '../../../../redux/dependenciesV2';
import { ensureArray, isTrue } from '../../../../utils';
import { useRevisionScanIdQueryParam } from '../../hooks/useRevisionScanIdQueryParam';
import type { DependencyQueryWithFilters } from '../../../../../shared/dependenciesV2';
import type { HydrateQueryState } from '../../../../redux/dependenciesV2/types';
import type { RouterProps } from '../../../../types';

import useDependencyMode from './useDependencyMode';
import useFetchDependencyAction from './useFetchDependencyAction';

type UseSyncStateAndURLParamsProps = RouterProps & {
  router: InjectedRouter;
};

/**
 * Transforms the query state into a query param string. Omitting default and undefined values.
 * For creating the URL search string
 * @param {DependencyQueryWithFilters} query state
 * @returns {string} search query string
 */
const stringifyQuery = (query: DependencyQueryWithFilters): string => {
  const {
    page: initialPage,
    count: initialCount,
    filters: allInitialFilters,
  } = DependenciesV2Store.initialState;

  const { title: initialTitle, ...initialFilters } = allInitialFilters;

  const { page, count, title, revisionScanId, ...filters } = query;

  const urlQuery: Partial<DependencyQueryWithFilters> = {
    title: title !== initialTitle ? title : undefined,
    page: page !== initialPage ? page : undefined,
    count: count !== initialCount ? count : undefined,
    revisionScanId,
    ...(!_.isEqual(filters, initialFilters) ? _.omitBy(filters, _.isEmpty) : {}),
  };

  return qs.stringify(urlQuery);
};

/**
 * Parses the URL query string into filter and pagination values.
 * Exported for testing.
 * @param {string} queryString search query string
 * @returns {HydrateQueryState} query state object
 */
export const parseQueryParams = (queryString: string): HydrateQueryState => {
  const parsed: Partial<HydrateQueryState> = qs.parse(queryString, {
    ignoreQueryPrefix: true,
  });

  const query = parsePaginationQueryParams<HydrateQueryState>(parsed);

  if (parsed.title) {
    query.title = parsed.title;
  }

  if (parsed.status) {
    query.status = ensureArray(parsed.status);
  }

  if (parsed.depth) {
    query.depth = ensureArray(parsed.depth);
  }

  if (parsed.layerDepth) {
    query.layerDepth = ensureArray(parsed.layerDepth);
  }

  if (parsed.hasIssues) {
    query.hasIssues = ensureArray(parsed.hasIssues);
  }

  if (parsed.licenses) {
    query.licenses = ensureArray(parsed.licenses);
  }

  if (parsed.fetchers) {
    query.fetchers = ensureArray(parsed.fetchers);
  }

  if (parsed.showIgnored) {
    query.showIgnored = isTrue(parsed.showIgnored);
  }

  if (parsed.rootProjects) {
    query.rootProjects = ensureArray(parsed.rootProjects);
  }

  if (parsed.packageLabels) {
    query.packageLabels = ensureArray(parsed.packageLabels);
  }

  if (parsed.v && ['2', '3'].includes(parsed.v)) {
    query.v = parsed.v;
  }

  if (parsed.sources) {
    query.sources = ensureArray(parsed.sources);
  }

  if (parsed.vendoredPath) {
    query.vendoredPath = parsed.vendoredPath;
  }

  return query;
};

/**
 * Hook used to keep the redux state and URL params in sync
 * @param {UseSyncStateAndURLParamsProps} Location / Router props
 */
export const useSyncStateAndURLParams = ({ router, location }: UseSyncStateAndURLParamsProps): void => {
  const queryState = useSelector(DependenciesV2Store.selectors.getFetchDependenciesQuery);
  const dependencyModeFromRoute = useDependencyMode();
  const { revisionScanId } = useRevisionScanIdQueryParam();

  const hydrateState = useFetchDependencyAction(DependenciesV2Store.actions.hydrateState);

  const syncUrlParams = useDebouncedCallback((pathname: string, query: DependencyQueryWithFilters) =>
    router.replace({
      pathname,
      search: `?${stringifyQuery(query)}`,
    }));

  /*
   * Hydrate state from URL params - this will update when the url changes.
   * It also runs when the selected revision scan changes, so the issues shown match that scan.
   */
  // TODO - also fetch deps here
  useEffect(() => {
    const parsed = parseQueryParams(location.search ?? '');
    // If no sources filter in URL, default based on route (e.g., /vendored -> Vendored)
    if (!parsed.sources && dependencyModeFromRoute) {
      parsed.sources = [dependencyModeFromRoute];
    }
    // Clear vendoredPath when not on vendored route to prevent it persisting across navigation
    if (dependencyModeFromRoute !== DependencySource.Vendored) {
      parsed.vendoredPath = undefined;
    }
    parsed.revisionScanId = revisionScanId;
    hydrateState(parsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, revisionScanId]);

  // Sync URL params with state - this will update when the redux state changes
  useEffect(() => {
    syncUrlParams(location.pathname, queryState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState]);

  // When the search query gets cleared, reset state persisting the revision
  useEffect(() => {
    if (location.search) {
      return;
    }

    const parsed = parseQueryParams('');
    // Apply route-based default for sources when search is cleared
    if (dependencyModeFromRoute) {
      parsed.sources = [dependencyModeFromRoute];
    }
    // Clear vendoredPath when not on vendored route
    if (dependencyModeFromRoute !== DependencySource.Vendored) {
      parsed.vendoredPath = undefined;
    }
    hydrateState(parsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
};
